'use client';

import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { CloseButton } from '../close-button/close-button';
import { Slot } from '../slot';
import { UpsellBannerRootContext } from './context/upsell-banner-root.context';
import { upsellBannerCloseButtonStyles } from './styles/upsell-banner-close-button.styles';
import type { UpsellBannerCloseButtonPropsInterface } from './types/upsell-banner-close-button.types';

/**
 * @since 10.0.0
 */
export const UpsellBannerCloseButton = forwardRef<
  HTMLButtonElement,
  UpsellBannerCloseButtonPropsInterface
>((props: UpsellBannerCloseButtonPropsInterface, forwardedRef) => {
  const { tone, open, onOpenChange } = useContext(UpsellBannerRootContext);
  const { children, className, onClick, ...rest } = props;

  const onClickHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      if (open) {
        onOpenChange?.(event);
      }

      onClick?.(event);
    },
    [open, onOpenChange, onClick],
  );

  const onImpact = tone && ['hero', 'impact'].includes(tone);

  return (
    <Slot
      ref={forwardedRef}
      data-c11n-version={__C11N_VERSION__}
      data-c11n-component="UpsellBanner.CloseButton"
      className={cx(css(upsellBannerCloseButtonStyles), className)}
      {...rest}
      onClick={onClickHandler}
    >
      {children || <CloseButton onImpact={onImpact} />}
    </Slot>
  );
});

UpsellBannerCloseButton.displayName = 'UpsellBanner.CloseButton';
